import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoryBrowser() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
      <div className="flex flex-col items-center text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {t('categories.title')}
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
          {t('categories.subtitle')}
        </p>
      </div>

      <div ref={ref} className="relative mx-auto mt-8 w-full max-w-md">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-lg border border-background-300/60 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{t('dropdown.label')}</span>
          <i className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"></i>
        </button>

        {open && (
          <ul
            className="absolute left-0 right-0 z-30 mt-2 max-h-72 overflow-auto rounded-lg border border-background-200 bg-background-50 py-1"
            role="listbox"
          >
            <li className="px-4 py-2 text-xs uppercase tracking-wider text-foreground-400">
              {t('dropdown.placeholder')}
            </li>
            {categories.map((cat) => (
              <li key={cat.key}>
                <a
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100">
                    <img
                      src={cat.image}
                      alt={`${t(`categories.${cat.key}`)} — USFANS spreadsheet category`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="flex-1">{t(`categories.${cat.key}`)}</span>
                  <i className="ri-arrow-right-up-line text-base text-foreground-400" aria-hidden="true"></i>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {categories.map((cat) => (
          <div
            key={cat.key}
            className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300"
            data-product-shop
          >
            <a href={cat.link} target="_blank" rel="noopener nofollow" className="block">
              <div className="relative h-40 w-full overflow-hidden md:h-44">
                <img
                  src={cat.image}
                  alt={`${t(`categories.${cat.key}`)} — USFANS spreadsheet category`}
                  title={`${t(`categories.${cat.key}`)} — USFANS Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-3">
                <span className="text-sm font-medium text-foreground-900">{t(`categories.${cat.key}`)}</span>
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-900 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-up-line text-sm" aria-hidden="true"></i>
                </span>
              </div>
            </a>
            <Link
              to={`/usfans-spreadsheet/${cat.key}`}
              className="flex items-center justify-center gap-1.5 border-t border-background-200/70 px-3 py-2 text-xs font-semibold text-primary-700 transition-colors hover:bg-background-100 whitespace-nowrap"
            >
              {t('guides.readMore')}
              <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}